/**
 * State — base class for every game-mode state in the v2 state machine.
 *
 * Mirrors UDG poker's `State` class: thin, with empty default lifecycle
 * hooks. All real behavior lives in concrete subclasses, where each
 * state's `onEnter` / `onUpdate` / `onExit` reads top-to-bottom and
 * makes direct calls into `this.game.<subsystem>` — no god-method, no
 * StateContext middleman.
 *
 * Subclasses override only the hooks they need. By default a state is
 * a no-op (useful for transitional beats like POINT_RESULT that exist
 * solely to break a same-state transition).
 *
 * Shared helpers live as protected `_methodName` members; promoted onto
 * this base class only when 2+ states genuinely share the logic. Until
 * then, prefer inlining inside the state — clarity over reuse, per the
 * UDG bias.
 */

import type { GameUpdate, CeeloResult, ComeOutOutcome, PointOutcome, CrapsPoint } from '@sdl/shared';
import {
  CeeloResultType,
  evaluateCeeloRoll,
  pickCeeloLabel,
  pickCrapsComeOutLabel,
  pickCrapsPointLabel,
} from '@sdl/shared';
import type { DiceResultUpdate } from '../../../ui/components/PlayerProfilesOverlay';
import { ceeloLabelKind, ceeloShooterVsBankerKind, crapsComeOutLabelKind, crapsPointLabelKind, type ResultLabelKind } from '../../dice/resultLabelKind';
import type { SDLGame } from './SDLGame.js';
import type { StateMachine } from './StateMachine.js';

export class State {
  protected stateMachine: StateMachine;
  protected game: SDLGame;

  constructor(stateMachine: StateMachine, game: SDLGame) {
    this.stateMachine = stateMachine;
    this.game = game;
  }

  /**
   * Called when entering this state. Receives the GameUpdate that
   * triggered the transition. Default is a no-op — override to set up
   * camera / wallet / instruction / animation chains.
   */
  async onEnter(_update: GameUpdate): Promise<void> {
    return;
  }

  /**
   * Called for every game:update while staying in the same state.
   * Override to react to data changes that matter for this phase
   * (timer, ready flags, fader list, etc). Avoid blanket re-rendering;
   * only touch the slices this state owns.
   */
  async onUpdate(_update: GameUpdate): Promise<void> {
    return;
  }

  /**
   * Called when leaving this state. The `update` passed in is the NEW
   * update (the one whose state differs from this state's). Read
   * `update.gameState` to make destination-aware decisions about which
   * exit animations to run (UDG pattern: `if (data.state !== 'flop') return`).
   */
  async onExit(_update: GameUpdate): Promise<void> {
    return;
  }

  // ──────────────────────────────────────────────────────────────────
  // SHARED HELPERS
  // Promoted onto this base only when 2+ states use them. Stay tiny.
  // ──────────────────────────────────────────────────────────────────

  /**
   * Whether we've already spawned the local roll for the CURRENT authorization.
   * Cleared the moment we lose authorization (see `_maybeSetupLocalRoll`) and on
   * a fresh state entry (`_resetRollSetup`).
   */
  private _rollSetupDone = false;

  /** Call at the top of a roll state's onEnter so a fresh entry re-arms the
   *  one-shot (the same state instance is reused across rounds/games). */
  protected _resetRollSetup(): void {
    this._rollSetupDone = false;
  }

  /**
   * Spawn the local player's roll setup the moment the server authorizes us —
   * the heart of "roll authorization is STATE". Authorization rides
   * `update.rollAuthorizedFor`, but the server populates it AFTER the
   * state-transition broadcast (the auto-authorize loop / HI_LO controller run
   * post-broadcast), so it usually arrives on a same-state follow-up update.
   * Calling this from BOTH onEnter and onUpdate covers either timing.
   *
   * Keyed on the AUTHORIZATION EDGE, not a round counter: we set up once while
   * authorized, and re-arm the instant we lose authorization. A roller always
   * goes authorized → (rolls, stops → server clears auth) → authorized again on
   * any re-roll, so this re-fires the setup for:
   *   - HI_LO tie re-rolls (controller re-authorizes the tied subset), and
   *   - craps POINT_ROLL continues (server re-authorizes the same shooter).
   * Crucially this does NOT rely on hiLoRounds / pointRolls — the craps
   * controller path never syncs hiLoRounds to the broadcast mid-phase, so a
   * round-counter key would never change and the re-roll would never spawn.
   *
   * Resume-safe: on state:full the server has already authorized, so the field
   * rides the enter broadcast and this fires from onEnter.
   */
  protected _maybeSetupLocalRoll(update: GameUpdate): void {
    const localId = this.game.getLocalPlayerId();
    if (!localId) return;
    const authorized = update.rollAuthorizedFor?.includes(localId) ?? false;
    if (!authorized) {
      // Not (or no longer) our roll — re-arm so the NEXT authorization (tie
      // re-roll / continue) spawns again. The server keeps us authorized from
      // authorization until our dice stop, so this only clears after our roll
      // completes, never mid-roll.
      this._rollSetupDone = false;
      return;
    }
    if (this._rollSetupDone) return;
    this._rollSetupDone = true;
    this.game.setupLocalRoll(update.gameState);
  }

  /**
   * Per-player reconciliation. For each player in the update:
   *
   *   - if `player.result` carries dice the local client hasn't shown
   *     yet, render them on the profile card. Animated updates fire
   *     the dice-result flyout (with label) from the dice center;
   *     non-animated updates (state-sync, late-join) place the icons
   *     instantly.
   *   - if `player.result` is null/empty, the profile card's icons
   *     clear naturally via the overlay's diff path.
   *
   * Dedup is keyed on (playerId, dice-values) so the same payload
   * arriving across multiple updates only fires once.
   *
   * States call this explicitly in onEnter / onUpdate / onExit at the
   * point where it's relevant (typically onEnter for late-join sync
   * and onUpdate for live in-state results). It's a helper, not a
   * hidden default — calling sites stay visible.
   */
  protected async _checkPlayers(update: GameUpdate): Promise<void> {
    const overlay = this.game.playerProfilesOverlay;
    if (!overlay) return;
    const players = update.players ?? [];
    if (players.length === 0) return;

    const targets = new Map<string, DiceResultUpdate | null>();

    // Cee-lo: the banker's roll (when present on this update) anchors the
    // shooters' win/point/loss stinger — a shooter's outcome is relative.
    const bankerResult = update.gameType === 'ceelo' && update.bankerId
      ? ((players.find((pl) => pl.playerId === update.bankerId)?.result ?? null) as CeeloResult | null)
      : null;

    for (const p of players) {
      const result = p.result as { dice?: number[] } | null;
      const dice = result?.dice;

      // Player has no result on this update → DON'T push a null target.
      // Pushing null tells updateDiceResults to animate the existing
      // icon out, which clobbers HI_LO single-die icons (those live in
      // liveDiceResults, not player.result) and any other icon that's
      // still meant to be visible across states. Round boundaries
      // explicitly clear icons via clearResultDedup + the WAITING /
      // SHOOTER_SET_STACK / BANKER_SET_POT entries in liveDiceResults.
      if (!dice || dice.length === 0) continue;

      // Cee-lo NOTHING (re-roll incoming) shouldn't render.
      if (
        update.gameType === 'ceelo' &&
        dice.length === 3 &&
        evaluateCeeloRoll(dice as [number, number, number]).type === CeeloResultType.NOTHING
      ) {
        continue;
      }

      const display = this.sortDiceForDisplay(dice, update.gameType);
      const alreadyShown = this.game.hasProcessedResult(p.playerId, dice);

      if (alreadyShown || !update.animated) {
        targets.set(p.playerId, { values: display });
        if (!alreadyShown) this.game.markResultProcessed(p.playerId, dice);
        continue;
      }

      this.game.markResultProcessed(p.playerId, dice);
      const screenPos = this.game.getDiceCenterScreen(p.playerId) ?? undefined;
      const label = this.pickLabelFor(update.gameType, p.result);
      const labelKind = this.pickLabelKindFor(
        update.gameType,
        p.result,
        p.playerId !== update.bankerId ? bankerResult : null,
      );
      targets.set(p.playerId, { values: display, screenPosition: screenPos, label, labelKind });
    }

    if (targets.size === 0) return;
    overlay.updateDiceResults(targets, update.animated);
    if (update.animated) await overlay.waitForAllFlyouts();
  }

  /**
   * Push the current player roster to the profile-cards overlay. Owns
   * the cross-subsystem stitching (local equipped dice from the dice
   * controller, opponent equipped dice from the opponent manager) so
   * states can call `_updateProfiles(update)` and not duplicate it.
   *
   * Called by EVERY state that needs the profile cards in sync — at
   * minimum onEnter (so the player list is correct on transition) and
   * onUpdate when joins / leaves / balance changes occur.
   */
  protected _updateProfiles(update: GameUpdate): void {
    const overlay = this.game.playerProfilesOverlay;
    if (!overlay) return;
    const localId = this.game.getLocalPlayerId();
    const localEquipped = this.game.diceController?.getEquippedDice().filter(Boolean) as string[] | undefined;
    overlay.refreshFromUpdate(update, {
      localPlayerId: localId,
      localEquippedDice: localEquipped,
      getOpponentEquippedDice: (id) => this.game.opponentDiceManager?.getOpponentEquippedDice(id),
      cdnBase: this.game.cdnBase,
    });
  }

  /**
   * For non-local rollers, spawn the shooter's ready-dice mock objects
   * in the 3D scene plus the floating "Player N" label above them.
   * Called from roll states' `onEnter` so spectators see the dice
   * pre-pickup with a name tag — matches cee-lo's pre-v2 behavior
   * during BANKER_ROLL / PLAYER_ROLL.
   *
   * Local rollers are skipped (their own dice are managed by
   * diceController). Returns silently if the opponent manager isn't
   * available yet — common during the very first scene load.
   */
  protected _showOpponentReadyDice(shooterId: string | null | undefined, count: number): void {
    if (!shooterId) return;
    const localId = this.game.getLocalPlayerId();
    if (shooterId === localId) return;
    const opp = this.game.opponentDiceManager;
    if (!opp) return;
    const positions = ((): Array<{ x: number; y: number; z: number }> => {
      switch (count) {
        case 1: return [{ x: -0.8, y: 1, z: 5 }];
        case 2: return [{ x: -0.75, y: 1, z: 5 }, { x: 0.75, y: 1, z: 5 }];
        case 3:
        default:
          return [
            { x: -1.5, y: 1, z: 5 },
            { x: 0,    y: 1, z: 5 },
            { x: 1.5,  y: 1, z: 5 },
          ];
      }
    })();
    // Single centered identity label via showReadyDice. DON'T also call
    // showResultLabel here — that's the HI_LO POST-stop label (placed
    // above the first active die for result framing). Both rendering
    // simultaneously gave us two labels per opponent — one centered,
    // one above the first die — which read as "a label on each die".
    opp.showReadyDice(shooterId, positions);
  }

  /**
   * Wipe the "BETTING" activity label on the top-center of the profiles
   * overlay. Every betting state's `onExit` should call this so the
   * pill clears the moment we leave the phase — otherwise it can ride
   * into the next state (e.g. into a roll) if the server's `isBetting`
   * reset broadcast hasn't landed yet. Idempotent.
   */
  protected _clearBettingActivity(): void {
    this.game.playerProfilesOverlay?.updateBettingActivity([]);
  }

  /**
   * Cee-lo dice arrange to canonical display order (pair-pair-point,
   * straight ascending, trips natural). Craps / HI_LO pass through.
   */
  protected sortDiceForDisplay(dice: number[], gameType: string): number[] {
    if (gameType !== 'ceelo' || dice.length !== 3) return [...dice];
    const sorted = [...dice].sort((a, b) => a - b);
    if (sorted[0] === 1 && sorted[1] === 2 && sorted[2] === 3) return sorted;
    if (sorted[0] === 4 && sorted[1] === 5 && sorted[2] === 6) return sorted;
    if (sorted[0] === sorted[2]) return sorted;       // trips
    if (sorted[0] === sorted[1]) return sorted;       // pair, point at end
    if (sorted[1] === sorted[2]) return [sorted[1], sorted[2], sorted[0]]; // pair, point at front → swap
    return sorted;
  }

  /**
   * Pick the showdown label for a player's result, dispatched by
   * gameType + result shape. Returns null if no label should display
   * (cee-lo NOTHING already filtered above; this handles craps
   * 'continue' and the unknown / forfeit-shaped results).
   */
  protected pickLabelFor(gameType: string, result: unknown): string | null {
    if (!result) return null;
    if (gameType === 'ceelo') {
      const r = result as CeeloResult;
      if (!r.type) return null;
      return pickCeeloLabel(r);
    }
    if (gameType === 'craps') {
      const r = result as
        | { kind: 'come_out'; outcome: ComeOutOutcome }
        | { kind: 'point_roll'; outcome: PointOutcome; point: CrapsPoint }
        | { kind: 'unknown' };
      if (r.kind === 'come_out') return pickCrapsComeOutLabel(r.outcome);
      if (r.kind === 'point_roll') return pickCrapsPointLabel(r.outcome, r.point);
      return null;
    }
    return null;
  }

  /**
   * Outcome family for the label picked above — drives which result-title
   * stinger plays when the label pops (win / point / loss). Mirrors
   * pickLabelFor's dispatch so sound and text always agree.
   *
   * `bankerResult` (cee-lo only): pass the banker's roll when classifying a
   * SHOOTER — their outcome is relative (rolling under the banker is a loss
   * even on a scoring point). Pass null for the banker's own roll.
   */
  protected pickLabelKindFor(
    gameType: string,
    result: unknown,
    bankerResult: CeeloResult | null = null,
  ): ResultLabelKind | null {
    if (!result) return null;
    if (gameType === 'ceelo') {
      const r = result as CeeloResult;
      if (!r.type) return null;
      if (bankerResult?.type && bankerResult.rank > 0) {
        return ceeloShooterVsBankerKind(r, bankerResult);
      }
      return ceeloLabelKind(r);
    }
    if (gameType === 'craps') {
      const r = result as
        | { kind: 'come_out'; outcome: ComeOutOutcome }
        | { kind: 'point_roll'; outcome: PointOutcome; point: CrapsPoint }
        | { kind: 'unknown' };
      if (r.kind === 'come_out') return crapsComeOutLabelKind(r.outcome);
      if (r.kind === 'point_roll') return crapsPointLabelKind(r.outcome);
      return null;
    }
    return null;
  }
}
